import { FC } from 'react';
import { useAtomValue } from 'jotai';
import { isNil } from 'lodash-es';
import { ExecutiveSummaryCard } from './executive-summary-card';
import { formatNumber } from 'utils/number';
import { executiveSummaryAtom } from '../../eval-3-workspace/atoms';
import { WELL_STATUS_COUNT_COLUMNS } from 'utils/slot-categories';
import style from './executive-summary.module.scss';

const PLACEHOLDER = '—';

const formatCurrency = (value: number) =>
  formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 0,
    style: 'currency',
    includeCommas: true,
  });

const formatDecimal = (value: number, precision = 2) =>
  formatNumber({
    value,
    minPrecision: precision,
    maxPrecision: precision,
    includeCommas: true,
  });

const formatCount = (value: number) =>
  formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 0,
    includeCommas: true,
  });

const renderCount = (value: number | undefined) => {
  return value === undefined ? PLACEHOLDER : formatCount(value);
};

const renderCurrency = (value: number | undefined) => {
  return value === undefined ? PLACEHOLDER : formatCurrency(value);
};

const renderDecimal = (value: number | undefined) => {
  return value === undefined ? PLACEHOLDER : formatDecimal(value);
};

interface Props {
  packageId: number;
}

const ExecutiveSummary: FC<Props> = ({ packageId }) => {
  const summary = useAtomValue(executiveSummaryAtom(packageId));

  // Split netAcres by netAcreageUnits so NRA and NMA each get their own card.
  // Backend ships per-instrument rows tagged with the unit type.
  const totalNraAcres = summary.acresByInstrumentType?.reduce(
    (sum, row) => (row.netAcreageUnits === 'NRA' ? sum + row.netAcres : sum),
    0
  );
  const totalNmaAcres = summary.acresByInstrumentType?.reduce(
    (sum, row) => (row.netAcreageUnits === 'NMA' ? sum + row.netAcres : sum),
    0
  );

  // The slot counts arrive as one section (pre-calc endpoint or the slots Pusher section), so
  // PDP's count stands for all of them.
  const slotCountsLoaded = summary.pdpSlotCount !== undefined;

  // Default to Broker Pricing (*Excluded) per the FE handoff doc. Falls
  // back to *Included when only SIM Valuation arrived.
  const targetValue = summary.targetNpvExcluded ?? summary.targetNpvIncluded;
  const maxValue = summary.maxNpvExcluded ?? summary.maxNpvIncluded;

  // Bundle the sentence inputs into an object that's either fully populated
  // or null, so TypeScript narrows the types inside the JSX guard below
  // without needing `!` non-null assertions on each field. `isNil` covers
  // both `null` and `undefined`.
  const summaryInputs =
    !isNil(summary.assetCount) &&
    !isNil(summary.operatorCount) &&
    !isNil(summary.formationCount) &&
    !isNil(totalNraAcres) &&
    !isNil(targetValue) &&
    !isNil(maxValue)
      ? {
          assetCount: summary.assetCount,
          operatorCount: summary.operatorCount,
          formationCount: summary.formationCount,
          totalNraAcres,
          targetValue,
          maxValue,
        }
      : null;

  return (
    <div className={style.container}>
      <div className={style.cardsRow}>
        <ExecutiveSummaryCard
          label="Broker Target Value"
          value={renderCurrency(targetValue)}
        />
        <ExecutiveSummaryCard
          label="Broker Max Value"
          value={renderCurrency(maxValue)}
        />
        <ExecutiveSummaryCard
          label="NRA"
          value={renderDecimal(totalNraAcres)}
        />
        <ExecutiveSummaryCard
          label="NMA"
          value={renderDecimal(totalNmaAcres)}
        />
        <ExecutiveSummaryCard
          label="Assets"
          value={renderCount(summary.assetCount)}
        />
        <ExecutiveSummaryCard
          label="PDP Slots"
          value={renderCount(summary.pdpSlotCount)}
        />
        <ExecutiveSummaryCard
          label="DUC Slots"
          value={renderCount(summary.ducSlotCount)}
        />
        <ExecutiveSummaryCard
          label="Permit Slots"
          value={renderCount(summary.permitSlotCount)}
        />
        {/* A Create New Well status card keeps its place, as a placeholder like the other cards,
            until the slot counts arrive, so the row doesn't shift when they do. Then only the
            statuses the package has slots in stay: most have none (and a run from before the four
            reports no count for them). */}
        {WELL_STATUS_COUNT_COLUMNS.filter(
          ({ key }) =>
            !slotCountsLoaded || (summary[`${key}SlotCount`] ?? 0) > 0
        ).map(({ key, label }) => (
          <ExecutiveSummaryCard
            key={key}
            label={`${label} Slots`}
            value={renderCount(summary[`${key}SlotCount`])}
          />
        ))}
        <ExecutiveSummaryCard
          label="LTD Slots"
          value={renderCount(summary.ltdSlotCount)}
        />
        <ExecutiveSummaryCard
          label="Operators"
          value={renderCount(summary.operatorCount)}
        />
        <ExecutiveSummaryCard
          label="Formations"
          value={renderCount(summary.formationCount)}
        />
      </div>
      {summaryInputs && (
        <p className={style.summarySentence}>
          This package contains{' '}
          <strong>{formatCount(summaryInputs.assetCount)} Assets</strong> across{' '}
          <strong>{formatCount(summaryInputs.operatorCount)} operator</strong>{' '}
          and{' '}
          <strong>
            {formatCount(summaryInputs.formationCount)} formations
          </strong>
          , representing{' '}
          <strong>{formatDecimal(summaryInputs.totalNraAcres)} NRA</strong>. The
          evaluated Broker Target Value is{' '}
          <strong>{formatCurrency(summaryInputs.targetValue)}</strong> and
          Broker Max Value is{' '}
          <strong>{formatCurrency(summaryInputs.maxValue)}</strong>.
        </p>
      )}
    </div>
  );
};

export { ExecutiveSummary };
